import { useEffect, useState, useCallback } from "react";
import { Trash2, X, Activity, ChevronDown, ChevronRight } from "lucide-react";
import {
  getRecentAnalyticsEvents,
  clearRecentAnalyticsEvents,
  type DebugAnalyticsEvent,
} from "@/lib/analytics";

const STORAGE_KEY = "analytics_debug_enabled";

function isDebugEnabled(): boolean {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("debug") === "analytics") {
      localStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    if (url.searchParams.get("debug") === "off") {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

const formatTime = (ts: number) => {
  const d = new Date(ts);
  return `${d.getHours().toString().padStart(2, "0")}:${d
    .getMinutes()
    .toString()
    .padStart(2, "0")}:${d.getSeconds().toString().padStart(2, "0")}.${d
    .getMilliseconds()
    .toString()
    .padStart(3, "0")}`;
};

const eventColor = (name: string) => {
  if (name === "page_view") return "text-sky-400";
  if (name.startsWith("lead_") || name === "generate_lead") return "text-emerald-400";
  if (name.startsWith("rideshare_")) return "text-amber-400";
  if (name.startsWith("booking_widget_")) return "text-fuchsia-400";
  if (name.includes("error") || name.includes("abandon")) return "text-rose-400";
  if (name === "phone_click" || name === "email_click") return "text-cyan-400";
  return "text-foreground";
};

function EventRow({ event }: { event: DebugAnalyticsEvent }) {
  const [open, setOpen] = useState(false);
  const hasParams = Object.keys(event.params).length > 0;
  return (
    <div className="border-b border-border/40 last:border-b-0">
      <button
        type="button"
        onClick={() => hasParams && setOpen((o) => !o)}
        className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-secondary/40 transition-colors"
      >
        <div className="pt-0.5 text-muted-foreground">
          {hasParams ? (
            open ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )
          ) : (
            <span className="inline-block w-3" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-mono text-xs font-semibold ${eventColor(event.name)}`}>
              {event.name}
            </span>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {formatTime(event.timestamp)}
            </span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded ${
                event.gtagSent
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-amber-500/20 text-amber-300"
              }`}
              title={event.gtagSent ? "Sent via gtag (GA4)" : "Queued in dataLayer only — GA4 gtag.js not loaded"}
            >
              {event.gtagSent ? "gtag" : "dataLayer"}
            </span>
          </div>
          {open && hasParams && (
            <pre className="mt-1.5 text-[10px] leading-relaxed bg-background/60 border border-border/40 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
              {JSON.stringify(event.params, null, 2)}
            </pre>
          )}
        </div>
      </button>
    </div>
  );
}

export default function AnalyticsDebugPanel() {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(true);
  const [events, setEvents] = useState<DebugAnalyticsEvent[]>([]);
  const [filter, setFilter] = useState("");

  // Initial enable check + keyboard shortcut (Ctrl/Cmd + Shift + A)
  useEffect(() => {
    setEnabled(isDebugEnabled());
    setEvents(getRecentAnalyticsEvents());

    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "a") {
        e.preventDefault();
        const next = !isDebugEnabled();
        if (next) localStorage.setItem(STORAGE_KEY, "1");
        else localStorage.removeItem(STORAGE_KEY);
        setEnabled(next);
        if (next) setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Subscribe to live events
  useEffect(() => {
    if (!enabled) return;
    const onEvent = () => setEvents(getRecentAnalyticsEvents());
    const onClear = () => setEvents([]);
    window.addEventListener("analytics:event", onEvent);
    window.addEventListener("analytics:debug-cleared", onClear);
    return () => {
      window.removeEventListener("analytics:event", onEvent);
      window.removeEventListener("analytics:debug-cleared", onClear);
    };
  }, [enabled]);

  const handleDisable = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setEnabled(false);
  }, []);

  if (!enabled) return null;

  const filtered = filter
    ? events.filter((e) => e.name.toLowerCase().includes(filter.toLowerCase()))
    : events;

  // Detect whether GA4/GTM are actually loaded for the status pill
  const gtmLoaded = typeof (window as Window & { google_tag_manager?: unknown }).google_tag_manager !== "undefined";
  const gtagLoaded = typeof window.gtag === "function";

  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] w-[380px] max-w-[calc(100vw-2rem)] rounded-xl border border-border/60 bg-background/95 backdrop-blur-xl shadow-2xl font-sans"
      role="region"
      aria-label="Analytics debug panel"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/40">
        <Activity className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Analytics Debug</span>
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded ${
            gtagLoaded ? "bg-emerald-500/20 text-emerald-300" : "bg-muted text-muted-foreground"
          }`}
          title="GA4 gtag.js status"
        >
          GA4 {gtagLoaded ? "✓" : "✗"}
        </span>
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded ${
            gtmLoaded ? "bg-emerald-500/20 text-emerald-300" : "bg-muted text-muted-foreground"
          }`}
          title="Google Tag Manager status"
        >
          GTM {gtmLoaded ? "✓" : "✗"}
        </span>
        <span className="text-[10px] text-muted-foreground ml-auto tabular-nums">
          {events.length}
        </span>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-muted-foreground hover:text-foreground"
          aria-label={open ? "Collapse" : "Expand"}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={clearRecentAnalyticsEvents}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Clear events"
          title="Clear events"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={handleDisable}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Disable debug panel"
          title="Disable (Ctrl/Cmd + Shift + A to re-enable)"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {open && (
        <>
          <div className="px-3 py-2 border-b border-border/40">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by event name…"
              className="w-full text-xs bg-secondary/40 border border-border/40 rounded px-2 py-1 outline-none focus:border-primary text-foreground placeholder:text-muted-foreground"
            />
          </div>

          <div className="max-h-[55vh] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                {events.length === 0
                  ? "Waiting for events… interact with the page to see them appear here."
                  : "No events match your filter."}
              </div>
            ) : (
              filtered.map((ev) => <EventRow key={ev.id} event={ev} />)
            )}
          </div>

          <div className="px-3 py-2 border-t border-border/40 text-[10px] text-muted-foreground leading-relaxed">
            <kbd className="font-mono px-1 py-0.5 bg-secondary/60 rounded border border-border/40">
              Ctrl/Cmd + Shift + A
            </kbd>{" "}
            to toggle. Append <code className="font-mono">?debug=off</code> to disable.
          </div>
        </>
      )}
    </div>
  );
}
